/**
 * Pre-loaded document templates — CDM, H&S policies, registers, certificates.
 *
 * A template defines:
 *   - id, title, category, scope, description
 *   - fields[]: structured data the user fills in
 *   - sections[]: prose blocks the user can edit
 *   - signatureRequired: bool (most policies are issued by a Director)
 *
 * The fill form generates a PDF with header (org logo + name), filled-in
 * fields, prose sections, optional signature block, and DRAFT disclaimer.
 *
 * IMPORTANT: review and adapt content before using on real CDM submissions.
 */

export type DocCategory = 'cdm' | 'policy' | 'register' | 'certificate' | 'custom';
export type DocScope = 'org' | 'site';

export interface DocFieldOption {
  value: string;
  label: string;
  hint?: string;       // plain-English explainer shown under the dropdown
}

export interface DocField {
  key: string;
  label: string;
  type: 'text' | 'textarea' | 'date' | 'number' | 'checkbox' | 'select' | 'tickList';
  placeholder?: string;
  hint?: string;
  required?: boolean;
  options?: DocFieldOption[];   // required when type === 'select'
  // tickList fields render as a multi-select checkbox group seeded with
  // these options, all pre-checked. User unticks anything that doesn't
  // apply and the joined string (' · '-separated) is stored.
  tickOptions?: string[];
}

export interface DocSection {
  heading: string;
  body: string;          // standard prose; user can edit (used when `items` not present)
  hint?: string;         // editor-only hint
  editable?: boolean;    // false = locked text (e.g. legal disclaimer)
  // Tick-first sections: when `items` is set, render as a tickable
  // bullet list instead of a free-text textarea. All items start checked
  // (these are the standard clauses for this template); user unticks
  // anything that doesn't apply on their site / for their business and
  // can add custom items via the Add input. At submit, the ticked subset
  // is serialised into the section body for the PDF.
  items?: string[];
}

export interface DocTemplate {
  id: string;
  title: string;
  category: DocCategory;
  scope: DocScope;
  description: string;
  fields: DocField[];
  sections: DocSection[];
  signatureRequired: boolean;          // signed by preparer / Director
  reviewCycleMonths?: number;          // suggested re-issue cadence
  expiryRequired?: boolean;            // certificates must capture expiry
}

export const DOC_TEMPLATES: DocTemplate[] = [
  // ============================================================
  // ORG-WIDE — Policies & Registers
  // ============================================================
  {
    id: 'hs_policy_statement',
    title: 'Health & Safety Policy Statement',
    category: 'policy',
    scope: 'org',
    description: 'Top-level H&S policy statement signed by a Director annually. By law (Health & Safety at Work Act), every business with 5 or more employees needs one — we generate yours.',
    fields: [
      { key: 'company_name', label: 'Company name', type: 'text', required: true },
      { key: 'director_name', label: 'Signing Director', type: 'text', required: true },
      { key: 'review_date', label: 'Next review date', type: 'date', required: true },
    ],
    sections: [
      { heading: 'Statement of intent', body: 'It is the policy of the organisation to ensure, so far as is reasonably practicable, the health, safety and welfare at work of all our employees and the safety of any others affected by our work. We are committed to: providing safe systems of work, safe plant and equipment, suitable training and supervision, adequate welfare facilities, and a culture in which our people feel able to raise concerns and have them addressed.' },
      { heading: 'Responsibilities', body: 'The Director(s) named above hold ultimate responsibility for health and safety. Day-to-day responsibility is delegated to site managers and competent supervisors. All employees and sub-contractors are responsible for taking reasonable care of their own and others\' health and safety, and for cooperating with the organisation on H&S matters.' },
      { heading: 'Arrangements', body: 'The detailed arrangements for putting this policy into effect — risk assessments, training, accident reporting, welfare, emergency procedures — are set out in the Health & Safety Arrangements Manual. This statement and the Arrangements are reviewed at least annually, and following any significant incident or change in operations.' },
    ],
    signatureRequired: true,
    reviewCycleMonths: 12,
  },
  {
    id: 'hs_arrangements',
    title: 'Health & Safety Arrangements Manual',
    category: 'policy',
    scope: 'org',
    description: 'The detailed how-we-do-it document that sits beneath the H&S Policy Statement. Covers risk assessment, training, accidents, welfare, emergencies.',
    fields: [
      { key: 'company_name', label: 'Company name', type: 'text', required: true },
      { key: 'document_owner', label: 'Document owner', type: 'text', required: true },
      { key: 'review_date', label: 'Next review date', type: 'date', required: true },
    ],
    sections: [
      { heading: 'Risk assessment', body: 'Significant hazards in our work are identified and recorded in task-specific Risk Assessments and Method Statements (RAMS). RAMS are reviewed before work starts on each site and adapted to site-specific conditions. Generic RAMS templates from our library are tailored on a per-project basis.' },
      { heading: 'Training & competence', body: 'All operatives hold the relevant cards / qualifications for their trade. Records are kept in our Training Register and reviewed for expiry on a rolling 30-day window. Subcontractors must demonstrate competence at induction (PQQ + card check).' },
      { heading: 'Accident reporting', body: 'All accidents, incidents, near-misses and dangerous occurrences are recorded in the digital Accident Book within 24 hours. RIDDOR-reportable incidents are reported to HSE within the timeframes set out in the Regulations. Investigations identify root causes and corrective actions; lessons are shared with the team.' },
      { heading: 'Welfare', body: 'Site welfare is provided to BS 1192 / Schedule 2 of the Construction (Design and Management) Regulations — toilets, hand-washing, drinking water, drying, rest area and changing where required. Site welfare provision is documented in the site-specific Welfare Plan.' },
      { heading: 'Emergency procedures', body: 'Site-specific emergency procedures (evacuation, fire, first aid, severe injury, near-miss escalation) are documented in the Site Emergency Procedures and the Site Fire Plan. All operatives are briefed at induction.' },
      { heading: 'Consultation', body: 'Employees are consulted on H&S matters via toolbox talks, monthly site meetings, and an open-door policy with management. Concerns may be raised confidentially via the Whistleblowing Statement.' },
    ],
    signatureRequired: true,
    reviewCycleMonths: 12,
  },
  {
    id: 'subcontractor_pqq',
    title: 'Sub-contractor Pre-Qualification Questionnaire (PQQ)',
    category: 'policy',
    scope: 'org',
    description: 'PQQ template for assessing sub-contractor competence before engagement. Captures insurance, training, RAMS and references.',
    fields: [
      { key: 'subcontractor_name', label: 'Sub-contractor name', type: 'text', required: true },
      { key: 'main_contact', label: 'Main contact', type: 'text', required: true },
      { key: 'date_issued', label: 'Date issued', type: 'date', required: true },
    ],
    sections: [
      {
        heading: 'Company information',
        hint: 'Fill in everything below — or paste in answers from the subbie\'s reply.',
        body: 'Registered company name and number:\n\nTrading address:\n\nMain contact (name, mobile, email):\n\nYears trading:\n\nTrade(s) covered:',
      },
      {
        heading: 'Insurance',
        hint: 'Type the figures and dates from their certificates.',
        body: 'Employers Liability — insurer, policy number, cover, expiry:\n\nPublic Liability — insurer, policy number, cover, expiry:\n\nProfessional Indemnity (if applicable) — insurer, policy number, cover, expiry:',
      },
      {
        heading: 'Training & competence',
        hint: 'List operatives with cards held + expiry dates. Add their supervisor qualification (SMSTS / SSSTS).',
        body: 'Operatives + cards held + expiry:\n\nSupervisor qualification (SMSTS / SSSTS / IOSH / NEBOSH):\n\nAny gaps / training booked:',
      },
      {
        heading: 'RAMS & H&S documentation',
        hint: 'Tick what the sub-contractor has provided. Untick anything missing.',
        body: '',
        items: [
          'Current H&S Policy provided (within review date)',
          'Employers Liability insurance certificate provided + in date',
          'Public Liability insurance certificate provided + in date',
          'RAMS templates provided for the trades they carry out',
          'CSCS / trade-specific cards seen for all named operatives',
          'SMSTS or SSSTS held by their site supervisor',
          'CDM 2015 awareness confirmed',
          'Sample previous RAMS reviewed and deemed competent',
          'COSHH assessments provided for any substances they bring on site',
          'Latest accident / RIDDOR record provided (or nil declaration)',
        ],
      },
      {
        heading: 'References',
        hint: 'Three projects of similar scale. Include client name + contact.',
        body: 'Reference 1 — project, client, contact:\n\nReference 2 — project, client, contact:\n\nReference 3 — project, client, contact:',
      },
      {
        heading: 'Decision',
        hint: 'Tick the boxes that describe your decision after reviewing the above.',
        body: '',
        items: [
          'Approved — engage without conditions',
          'Approved with conditions (note conditions below)',
          'Not approved — material concerns documented below',
          'Insurances re-checked before each new project',
          'PQQ to be refreshed annually',
        ],
      },
    ],
    signatureRequired: true,
    reviewCycleMonths: 12,
  },
  {
    id: 'cdm_compliance_statement',
    title: 'CDM Compliance Statement',
    category: 'cdm',
    scope: 'org',
    description: 'Statement confirming the organisation\'s competence, capability and arrangements to fulfil its duties under CDM 2015.',
    fields: [
      { key: 'company_name', label: 'Company name', type: 'text', required: true },
      {
        key: 'cdm_role',
        label: 'Your CDM role on this project',
        type: 'select',
        required: true,
        hint: 'Not sure which fits? Most builders are either Principal Contractor (when other trades are also on site) or Contractor (when you\'re the only firm on site).',
        options: [
          { value: 'Principal Contractor', label: 'Principal Contractor', hint: 'You\'re the main builder and there are other trades on site (electricians, plumbers, roofers etc.). You\'re in charge of site safety.' },
          { value: 'Contractor',           label: 'Contractor',           hint: 'You\'re the only firm on site, OR you work under another main contractor. You manage your own safety, not the whole site.' },
          { value: 'Principal Designer',   label: 'Principal Designer',   hint: 'You designed the project AND co-ordinate other designers. Rare for builders — usually an architect or structural engineer.' },
          { value: 'Designer',             label: 'Designer',             hint: 'You designed part of the works (e.g. a structural detail, an extension layout). Less common for builders.' },
          { value: 'Client',               label: 'Client',               hint: 'You\'re building for yourself / your own company. Rare to pick this one.' },
        ],
      },
      { key: 'director_name', label: 'Signing Director', type: 'text', required: true },
    ],
    sections: [
      { heading: 'CDM role(s) held', body: 'We act in the role(s) noted above on projects where we hold the relevant duties under CDM 2015.' },
      { heading: 'Competence & capability', body: 'Our personnel hold the qualifications, cards and experience required for the trades and roles undertaken. CITB / IOSH / NEBOSH qualifications for supervisors. Records held in the Training Register.' },
      {
        heading: 'Arrangements',
        body: '',
        hint: 'Tick the CDM arrangements your business has in place.',
        items: [
          'Pre-construction information is reviewed for every project',
          'Construction Phase Plans are produced before work starts',
          'Health & Safety File contributions are made on completion',
          'Sub-contractors are pre-qualified via our PQQ before engagement',
          'Risk assessments and method statements (RAMS) are produced for every task',
          'Site inductions are mandatory for every operative and visitor',
          'Toolbox talks are delivered weekly on active sites',
          'Daily / weekly site inspections are recorded and actioned',
        ],
      },
      { heading: 'Continuous improvement', body: 'Lessons learned from incidents, near-misses, audits and inspections feed into our procedures. Annual review of this statement and supporting policies.' },
    ],
    signatureRequired: true,
    reviewCycleMonths: 12,
  },
  {
    id: 'fire_safety_policy',
    title: 'Fire Safety Policy',
    category: 'policy',
    scope: 'org',
    description: 'Fire safety policy compliant with the Regulatory Reform (Fire Safety) Order 2005 — applies to workplaces and shared sites.',
    fields: [
      { key: 'company_name', label: 'Company name', type: 'text', required: true },
      { key: 'responsible_person', label: 'Responsible person', type: 'text', placeholder: 'Director or named manager', required: true },
    ],
    sections: [
      { heading: 'Policy statement', body: 'The organisation will take reasonable steps to prevent fires, protect life if a fire does occur, and ensure that property and the environment are protected so far as is reasonably practicable. The Responsible Person under the FSO is named above.' },
      { heading: 'Fire risk assessment', body: 'A fire risk assessment is produced for each site we control and reviewed at least annually or after any significant change. Site-specific arrangements are documented in the Site Fire Plan.' },
      { heading: 'Fire prevention', body: 'Hot Works Permits are mandatory for any cutting, welding, brazing or grinding work. Combustibles are kept clear of heat sources. Fire watch periods after hot works. Smoking is prohibited except in designated areas.' },
      { heading: 'Fire detection & alarm', body: 'Detection and alarm appropriate to the site is provided per the Site Fire Plan. Tested weekly during occupation. All operatives briefed at induction.' },
      { heading: 'Evacuation', body: 'Evacuation routes, muster points and roll-call procedure documented in the Site Fire Plan. Drills as appropriate to the project duration.' },
      { heading: 'Equipment', body: 'Suitable extinguishers (CO₂ and foam minimum) at each site. Annually serviced. Hot-works locations carry a CO₂ extinguisher within reach.' },
    ],
    signatureRequired: true,
    reviewCycleMonths: 12,
  },
  {
    id: 'first_aid_policy',
    title: 'First Aid Policy',
    category: 'policy',
    scope: 'org',
    description: 'First aid arrangements per the Health and Safety (First-Aid) Regulations 1981.',
    fields: [
      { key: 'company_name', label: 'Company name', type: 'text', required: true },
      { key: 'fa_lead', label: 'First Aid Lead', type: 'text', required: true },
    ],
    sections: [
      { heading: 'Policy statement', body: 'The organisation provides first-aid equipment, facilities and trained personnel sufficient for our workforce and the work we do.' },
      { heading: 'Risk assessment', body: 'A first aid needs assessment is conducted for each site, considering: number of people, nature of work, hazards present, distance to A&E, lone working. Recorded in the Site Setup register.' },
      { heading: 'Provision', body: 'BS 8599-1 first aid kit at every site (medium minimum). Eye-wash station for any work involving cement / chemicals. AED location identified for sites within 4 minutes of one. Trained first-aiders on each site (Emergency First Aid at Work as a minimum, First Aid at Work for higher-risk sites).' },
      { heading: 'Reporting', body: 'All first-aid interventions are recorded in the Accident Book. Significant incidents trigger a near-miss / accident form and (if applicable) RIDDOR notification.' },
    ],
    signatureRequired: true,
    reviewCycleMonths: 12,
  },
  {
    id: 'driving_for_work_policy',
    title: 'Driving for Work Policy',
    category: 'policy',
    scope: 'org',
    description: 'Driving for work policy — covers licence checks, vehicle condition, fatigue, mobile phones.',
    fields: [
      { key: 'company_name', label: 'Company name', type: 'text', required: true },
    ],
    sections: [
      { heading: 'Scope', body: 'This policy applies to all employees and sub-contractors driving on company business — own vehicle, company vehicle or hired.' },
      { heading: 'Licence and insurance', body: 'A valid full UK / EU licence appropriate for the vehicle is required. Licences are checked annually via the DVLA share-licence service. Insurance must include business use for own-vehicle drivers.' },
      { heading: 'Vehicle condition', body: 'Pre-use checks before any journey: tyres, lights, mirrors, fluids, load. Defects reported and rectified before further use. Annual MOT and service records kept on file for company vehicles.' },
      { heading: 'Fatigue & journey planning', body: 'No journey to start if the driver is fatigued. Breaks of 15 min every 2 hours on long journeys. Daily driving capped at 9 hours where reasonably practicable. Weather considered before departure.' },
      { heading: 'Mobile phones & in-cab distractions', body: 'Hand-held mobile use whilst driving is prohibited. Hands-free is permitted but discouraged for sustained calls. SatNav set before departure, not during.' },
      { heading: 'Drugs & alcohol', body: 'Zero tolerance — see the Drugs & Alcohol Policy.' },
    ],
    signatureRequired: true,
    reviewCycleMonths: 12,
  },
  {
    id: 'drugs_alcohol_policy',
    title: 'Drugs & Alcohol Policy',
    category: 'policy',
    scope: 'org',
    description: 'Drugs and alcohol policy — zero-tolerance for safety-critical work.',
    fields: [
      { key: 'company_name', label: 'Company name', type: 'text', required: true },
    ],
    sections: [
      { heading: 'Policy', body: 'No employee or sub-contractor may be at work under the influence of drugs or alcohol. Construction is safety-critical work; impairment can cause fatalities. Consumption on site is prohibited at all times.' },
      { heading: 'Prescription medication', body: 'Anyone taking prescription medication that may impair judgement, reaction time or concentration must inform their supervisor before starting work, so that duties can be adjusted where required.' },
      { heading: 'Testing', body: 'Pre-employment testing applies to safety-critical roles. Random and for-cause testing may be carried out on company sites, in accordance with this policy and statutory rights.' },
      { heading: 'Support', body: 'Anyone identifying that they have a problem with drugs or alcohol is encouraged to seek help. Confidential support is available through our employee assistance programme.' },
      { heading: 'Disciplinary', body: 'Breach of this policy is treated as gross misconduct and may result in immediate dismissal.' },
    ],
    signatureRequired: true,
    reviewCycleMonths: 12,
  },
  {
    id: 'lone_working_policy',
    title: 'Lone Working Policy',
    category: 'policy',
    scope: 'org',
    description: 'Lone working risk-management policy — applies to surveys, callouts, after-hours work.',
    fields: [
      { key: 'company_name', label: 'Company name', type: 'text', required: true },
    ],
    sections: [
      { heading: 'Scope', body: 'Applies to any employee or sub-contractor working alone on a site or alone away from a site (e.g. surveys, snagging, callouts).' },
      { heading: 'Risk assessment', body: 'Lone working is risk-assessed before each instance. High-risk lone work is prohibited (working at height alone, confined space alone, hot works alone, work with live electrical). For lower-risk lone work, the assessment must justify why pair-working is not practicable.' },
      { heading: 'Communication', body: 'Lone workers must carry a charged mobile phone. Check-in calls at agreed intervals (typically 2-hour) with a designated point of contact. Failure to check in triggers an escalation procedure ending in a welfare check.' },
      { heading: 'After-hours work', body: 'After-hours lone work requires advance notice to the supervisor and the same check-in procedure.' },
    ],
    signatureRequired: true,
    reviewCycleMonths: 12,
  },
  {
    id: 'gdpr_data_protection',
    title: 'GDPR / Data Protection Policy',
    category: 'policy',
    scope: 'org',
    description: 'Data protection policy summary — UK GDPR / Data Protection Act 2018.',
    fields: [
      { key: 'company_name', label: 'Company name', type: 'text', required: true },
      { key: 'ico_number', label: 'ICO registration number', type: 'text' },
      { key: 'dpo', label: 'Data Protection contact', type: 'text', required: true },
    ],
    sections: [
      { heading: 'Lawful basis', body: 'Personal data is processed only where there is a lawful basis under UK GDPR — typically performance of a contract (employees, sub-contractors), legal obligation (RIDDOR, accident records), or legitimate interest (site sign-in for security and welfare).' },
      { heading: 'Data we hold', body: 'Employee and sub-contractor records (names, contact details, training records, accident reports). Site visitor sign-in records. Photos taken on site for record purposes. Customer records for contracted work.' },
      { heading: 'Storage and retention', body: 'Personal data is stored within the UK / EEA. Accident records retained for 3 years from the date of injury (or until 21 if the injured person is a minor). Training records retained for the duration of employment plus 6 years. Other records retained per their statutory or contractual retention period.' },
      { heading: 'Subject rights', body: 'Individuals have the right to access, correct, erase or restrict the processing of their data. Requests should be made in writing to the Data Protection contact named above.' },
      { heading: 'Breach response', body: 'Suspected breaches are investigated within 24 hours. Notifiable breaches are reported to the ICO within 72 hours.' },
    ],
    signatureRequired: true,
    reviewCycleMonths: 12,
  },
  {
    id: 'whistleblowing_statement',
    title: 'Whistleblowing Statement',
    category: 'policy',
    scope: 'org',
    description: 'Confidential whistleblowing channel — Public Interest Disclosure Act 1998.',
    fields: [
      { key: 'company_name', label: 'Company name', type: 'text', required: true },
      { key: 'contact_route', label: 'Confidential contact route', type: 'text', placeholder: 'Email / phone / letter to named Director', required: true },
    ],
    sections: [
      { heading: 'Purpose', body: 'The organisation is committed to honest, lawful and ethical conduct. Anyone who has a genuine concern about wrongdoing — H&S breaches, fraud, bribery, discrimination or any unlawful act — is encouraged to raise it.' },
      { heading: 'Protection', body: 'Whistleblowers acting in good faith are protected from detriment under the Public Interest Disclosure Act 1998. Reports may be made confidentially via the route above. Anonymous reports are accepted but harder to investigate.' },
      { heading: 'Escalation', body: 'Where the matter cannot be addressed internally, or where the wrongdoing involves senior management, concerns may be raised with the relevant prescribed regulator (HSE, ICO, HMRC, etc.).' },
    ],
    signatureRequired: true,
    reviewCycleMonths: 12,
  },
  {
    id: 'equality_diversity_policy',
    title: 'Equality & Diversity Policy',
    category: 'policy',
    scope: 'org',
    description: 'Equality, diversity and anti-discrimination policy — Equality Act 2010.',
    fields: [
      { key: 'company_name', label: 'Company name', type: 'text', required: true },
    ],
    sections: [
      { heading: 'Statement of intent', body: 'The organisation is committed to equality of opportunity. We do not discriminate on the basis of any protected characteristic under the Equality Act 2010 — age, disability, gender reassignment, marriage / civil partnership, pregnancy / maternity, race, religion or belief, sex, or sexual orientation.' },
      { heading: 'Recruitment', body: 'Recruitment is on merit. Job descriptions describe the role; person specifications describe the requirements. Adjustments are made for candidates with disabilities throughout the process.' },
      { heading: 'In employment', body: 'Bullying, harassment and discrimination are not tolerated. Reasonable adjustments are made for employees with disabilities. Training is offered equally.' },
      { heading: 'Sub-contractors and supply chain', body: 'We expect equivalent standards from our sub-contractors and suppliers. Concerns raised by employees about a third party are investigated.' },
    ],
    signatureRequired: true,
    reviewCycleMonths: 12,
  },
  {
    id: 'anti_slavery_statement',
    title: 'Anti-Slavery Statement',
    category: 'policy',
    scope: 'org',
    description: 'Modern Slavery Act 2015 statement — required for businesses above £36m turnover, recommended for any.',
    fields: [
      { key: 'company_name', label: 'Company name', type: 'text', required: true },
      { key: 'financial_year', label: 'Financial year', type: 'text', required: true },
    ],
    sections: [
      { heading: 'Statement', body: 'The organisation is committed to acting ethically and with integrity in all our business dealings. We have a zero-tolerance approach to modern slavery and human trafficking, and have taken steps to ensure they do not occur in our business or supply chain.' },
      { heading: 'Our approach', body: 'Sub-contractors are pre-qualified before engagement. Recruitment is via known agencies or direct application. Right-to-work checks are carried out for every employee. Concerns are escalated via the Whistleblowing route.' },
      { heading: 'Training', body: 'Managers and supervisors receive guidance on identifying signs of modern slavery — workers without ID, signs of coercion, identical addresses for multiple workers. Concerns are reported to the police and the Modern Slavery Helpline.' },
    ],
    signatureRequired: true,
    reviewCycleMonths: 12,
  },
  // NOTE: 'insurance_certificates' template removed in May 2026 — insurance
  // details belong on the Org Settings page (or attached to the Org as
  // structured fields), not as a generated "doc" in the Documents library.
  // PDFs of the actual certificates are uploaded directly there; this
  // template was a wrapper that didn't add value.

  // ============================================================
  // SITE-SPECIFIC — CDM & operational
  // ============================================================
  {
    id: 'pre_construction_information',
    title: 'Pre-Construction Information (PCI)',
    category: 'cdm',
    scope: 'site',
    description: 'Pre-Construction Information (PCI) provided by the Client / Principal Designer — collated for the site file. Captures known hazards, services, asbestos surveys, ground conditions etc.',
    fields: [
      { key: 'project_name', label: 'Project name', type: 'text', required: true },
      { key: 'client', label: 'Client', type: 'text', required: true },
      { key: 'principal_designer', label: 'Principal Designer', type: 'text' },
      { key: 'pci_date', label: 'PCI (Pre-Construction Information) issued', type: 'date', required: true },
    ],
    sections: [
      { heading: '1. Project description', body: 'Brief description of the project, scope of works, expected duration and key dates.' },
      { heading: '2. Client requirements', body: 'Project-specific requirements — boundaries, restrictions, working hours, neighbouring properties, environment.' },
      { heading: '3. Existing site information', body: 'Asbestos register / survey reference. Existing services (gas, electric, water, comms, drains). Ground conditions, any contamination. Existing structures and their condition.' },
      { heading: '4. Significant H&S issues', body: 'Specific hazards known from the existing site or design — unusual structural features, hazardous materials, traffic, access constraints, working at height, confined spaces.' },
      { heading: '5. Health & Safety File', body: 'Where the H&S File is maintained and what contributions are required from the Principal Contractor on completion.' },
    ],
    signatureRequired: false,
  },
  {
    id: 'construction_phase_plan',
    title: 'Construction Phase Plan (CPP)',
    category: 'cdm',
    scope: 'site',
    description: 'Construction Phase Plan required under CDM 2015 reg. 12 / 15 — produced by the Principal Contractor before work starts on site. The big one.',
    fields: [
      { key: 'project_name', label: 'Project name', type: 'text', required: true },
      { key: 'site_address', label: 'Site address', type: 'text', required: true },
      { key: 'client', label: 'Client', type: 'text', required: true },
      { key: 'pc_name', label: 'Principal Contractor', type: 'text', required: true },
      { key: 'pd_name', label: 'Principal Designer', type: 'text' },
      { key: 'site_manager', label: 'Site manager (name & mobile)', type: 'text', required: true },
      { key: 'start_date', label: 'Construction start', type: 'date', required: true },
      { key: 'completion_date', label: 'Anticipated completion', type: 'date', required: true },
      { key: 'f10_reference', label: 'F10 notification ref (if notifiable)', type: 'text', hint: 'Job over 30 days with 20+ workers at once, OR over 500 person-days? Submit F10 at https://form.hse.gov.uk/f10 and put the reference here.' },
    ],
    // Sections follow the CPP contents list in HSE L153 (CDM 2015
    // guidance) Appendix 3: description of the project, management of the
    // work, arrangements for controlling significant site risks, and the
    // H&S File. Sections 1 is free text (must be written for the site);
    // every other section is tick-first with standard clauses, and can be
    // marked "Not applicable to this project".
    sections: [
      { heading: '1. Description of project', body: '', hint: 'Write this for the site: what is being built, where, the main phases of work and key dates. It cannot be left as standard.' },
      { heading: '2. Project team & responsibilities', body: '', items: [
        'Client, Principal Designer and Principal Contractor named on the cover of this plan',
        'Designers and consultants (architect, structural engineer, M&E) listed with contact details in the project directory',
        'Named site manager in day-to-day control of the site, as shown on the cover',
        'Site supervisors appointed for each trade package',
        'Everyone knows who to report H&S concerns to — names on the site notice board',
      ]},
      { heading: '3. Existing information & site survey', body: '', items: [
        'Pre-construction information from the client / Principal Designer received and reviewed',
        'Existing drawings and as-built records held on site',
        'Asbestos survey reviewed (refurbishment / demolition survey where relevant)',
        'Ground investigation / site survey reviewed',
        'Services drawings (gas, electric, water, drainage, telecoms) obtained',
        'Neighbouring properties, access constraints and restrictions noted',
      ]},
      { heading: '4. Management of the work', body: '', items: [
        'Programme issued and kept up to date',
        'H&S aim: zero accidents and no harm to workers or the public',
        'Weekly progress / coordination meeting including H&S',
        'Design changes agreed with the Principal Designer before work proceeds',
        'Weekly recorded site inspection by the site manager',
        'Daily site walk to check housekeeping, access and plant',
        'Site-specific RAMS reviewed and approved before each task starts',
        'Permits to work for hot works, digging, confined spaces and live services',
      ]},
      { heading: '5. Consulting & communicating with workers', body: '', items: [
        'Site induction for everyone before starting work',
        'Weekly toolbox talks, with attendance recorded',
        'Daily start-of-shift briefing on the day\'s work and hazards',
        'Workers can raise H&S concerns with the site manager at any time',
        'H&S information displayed on the site notice board (F10, HSE poster, emergency contacts)',
        'Changes to RAMS briefed to the workers affected before work continues',
      ]},
      { heading: '6. Induction, training & competence', body: '', items: [
        'All operatives hold a valid CSCS card (or equivalent) for their trade',
        'Plant operators hold CPCS / NPORS for the plant they use',
        'Scaffolders CISRS; MEWP operators IPAF; tower users PASMA',
        'Training and card copies held on file',
        'Supervisors hold SSSTS / SMSTS where appropriate',
        'Young or inexperienced workers supervised at all times',
      ]},
      { heading: '7. Sub-contractor selection & control', body: '', items: [
        'Sub-contractors checked for competence and insurance before appointment',
        'RAMS received, reviewed and accepted before work starts',
        'Sub-contractors attend induction and toolbox talks',
        'Sub-contractor work supervised and inspected by the site manager',
        'Sub-contractors share H&S information with other trades affected by their work',
      ]},
      {
        heading: '8. Site rules',
        body: '',
        hint: 'Tick the site rules in force on this project. Untick any that don\'t apply, add custom rules below.',
        items: [
          'Working hours: 8am – 5pm Monday to Friday',
          'No working on Sundays or bank holidays without prior agreement',
          'PPE mandatory at all times: hard hat, hi-vis, safety boots',
          'Cut-resistant gloves for any handling task',
          'Eye protection mandatory for drilling, cutting, breaking',
          'No smoking on site (designated area only if permitted)',
          'No alcohol or drugs on site — zero tolerance',
          'Mobile phones only in welfare area, not on active work',
          'All visitors must sign in and receive induction',
          'No music or earphones on site',
          'Site speed limit 5 mph',
          'Tidy as you go — daily housekeeping',
          'Welfare to be left clean each day',
        ],
      },
      { heading: '9. Site security & protecting the public', body: '', items: [
        'Site boundary fenced / hoarded and gates locked out of hours',
        'Warning signs at every entrance',
        'Plant immobilised and keys removed at the end of each day',
        'Ladders removed or boarded out of hours',
        'Excavations and openings covered or barriered',
        'Public footpaths kept clear or safely diverted',
        'Neighbours informed of noisy works and large deliveries',
      ]},
      { heading: '10. Welfare arrangements', body: '', items: [
        'Toilets and hand-washing with hot and cold water, soap and towels',
        'Drinking water available throughout the day',
        'Heated rest area with seating and means to heat food and drink',
        'Drying and changing area for wet or dirty work',
        'Welfare in place before work starts and cleaned daily',
        'Full details in the Site Welfare Plan',
      ]},
      { heading: '11. Fire & emergency procedures', body: '', items: [
        'Fire alarm / air horn and assembly point shown on the site plan',
        'Extinguishers at welfare, stores and each working level',
        'Escape routes kept clear and signed',
        'Hot works permit system with 60-minute fire watch',
        'Flammable materials stored away from buildings and ignition sources',
        'Emergency contacts and site address displayed for 999 calls',
        'Full details in the Site Fire Plan',
      ]},
      { heading: '12. First aid', body: '', items: [
        'Trained first-aider on site whenever work is taking place',
        'First-aid kit in the welfare cabin, checked weekly',
        'Eyewash available',
        'Nearest A&E and route displayed on the notice board',
        'First-aiders\' names displayed on the notice board',
      ]},
      { heading: '13. Accident & incident reporting', body: '', items: [
        'All accidents, near misses and dangerous occurrences recorded in the app on the day',
        'Serious incidents reported to the site manager and director immediately',
        'RIDDOR-reportable incidents reported to HSE within the legal deadline',
        'Incidents investigated and actions to prevent recurrence recorded',
        'Lessons learned shared at the next toolbox talk',
      ]},
      {
        heading: '14. Traffic, deliveries & storage',
        body: '',
        hint: 'Tick the traffic management controls on this site.',
        items: [
          'One-way traffic flow where possible',
          'Designated entry / exit points',
          'Segregated pedestrian routes from welfare to work areas',
          'Banksman for all reverse manoeuvres',
          'Banksman for all lifting operations',
          'Reverse alarms and beacons on all plant',
          '5 mph site speed limit',
          'No-go zones marked around plant and excavations',
          'Deliveries booked in and supervised on arrival',
          'Materials stored safely, stacked stable and away from edges',
          'Public footpath / road — signage, fencing, banksman during deliveries',
          'Full Traffic Management Plan on file for this site',
        ],
      },
      { heading: '15. Existing services', body: '', items: [
        'Services located from drawings and CAT & Genny scan before any digging',
        'Permit to dig required for all excavation',
        'Overhead power lines identified — goalposts / barriers where plant works nearby',
        'Services isolated or protected before work near them',
        'Hand-dig within 0.5 m of any located service',
      ]},
      {
        heading: '16. Working at height',
        body: '',
        hint: 'Tick the working-at-height controls in place. Untick anything not applicable on this site.',
        items: [
          'Scaffold provider holds CISRS / equivalent competence',
          'Scaffold inspected before first use + weekly + after alteration',
          'Scaffold tags visible at every access point',
          'Mobile towers used only by trained operatives (PASMA)',
          'MEWPs used only by IPAF card holders',
          'Harness + lanyard required for any unprotected edge work',
          'Edge protection (handrail + toeboard) on all working platforms',
          'Roof work and fragile surfaces risk-assessed separately with a specific RAMS',
          'No ladders for two-handed work',
          'Rescue plan in place for harness work',
        ],
      },
      { heading: '17. Excavations', body: '', items: [
        'Excavations over 1.2 m supported, battered back or stepped',
        'Edge protection / barriers around all excavations',
        'Spoil and materials kept at least 1 m from the edge',
        'Safe ladder access to excavations',
        'Excavations inspected at the start of each shift and after any event affecting stability',
        'Stop blocks for vehicles tipping into excavations',
      ]},
      { heading: '18. Temporary works', body: '', items: [
        'Temporary works coordinator appointed',
        'Temporary works (props, shoring, formwork, needling) designed by a competent person',
        'Temporary works register kept on site',
        'Temporary works checked before loading and before removal',
      ]},
      { heading: '19. Demolition & structural stability', body: '', items: [
        'Demolition / structural alterations planned in writing by a competent person',
        'Structural engineer\'s details followed for openings and removal of walls',
        'Temporary support in place before any structural element is removed',
        'Exclusion zone around demolition work',
      ]},
      { heading: '20. Confined spaces', body: '', items: [
        'Confined spaces identified (manholes, tanks, voids)',
        'Entry avoided where possible; permit to work where not',
        'Atmosphere tested before and during entry',
        'Trained operatives and rescue arrangements in place before entry',
      ]},
      { heading: '21. Plant & lifting', body: '', items: [
        'Plant operated only by trained, authorised operators',
        'Daily pre-use checks recorded',
        'Thorough examination (LOLER) certificates on file for lifting equipment',
        'Lifting operations planned; lift plan for anything non-routine',
        'Plant and pedestrians segregated',
      ]},
      {
        heading: '22. Hazardous substances',
        body: '',
        hint: 'Tick the COSHH controls on this site.',
        items: [
          'COSHH register maintained for all substances on site',
          'Safety Data Sheets (SDS) on file for every substance',
          'Substances stored in lockable COSHH cabinet',
          'Spill kit and absorbent granules on site',
          'No mixing of substances except per manufacturer instruction',
          'PPE matched to each substance per its SDS',
          'Disposal via licensed waste carrier only',
          'No food / drink in storage areas',
        ],
      },
      { heading: '23. Asbestos', body: '', items: [
        'Asbestos survey reference recorded and survey held on site',
        'Known asbestos marked and protected; no work on it by our operatives',
        'Stop work and evacuate the area if suspect material is uncovered',
        'Licensed contractor used for any licensable asbestos removal',
        'Asbestos awareness training for all operatives',
      ]},
      { heading: '24. Dust & silica', body: '', items: [
        'Cutting done with water suppression or on-tool extraction',
        'FFP3 masks, face-fit tested, for dusty work',
        'H-class / M-class vacuum for clean-up — no dry sweeping',
        'Dusty work kept away from other trades and the public',
      ]},
      { heading: '25. Noise & vibration', body: '', items: [
        'Quieter tools and methods chosen where possible',
        'Hearing protection zones marked around noisy work',
        'Hand-arm vibration exposure planned and tool time limited',
        'Low-vibration tools and regular breaks',
        'Neighbours informed; noisy work kept within agreed hours',
      ]},
      { heading: '26. Manual handling', body: '', items: [
        'Mechanical aids (hoists, telehandler, trolleys) used for heavy items',
        'Blocks over 20 kg lifted by two people or mechanically',
        'Materials delivered as close to point of use as possible',
        'Manual handling covered at induction',
      ]},
      { heading: '27. Contaminated land', body: '', items: [
        'Ground investigation reviewed for contamination',
        'Stop work and seek advice if unexpected contamination is found',
        'Welfare and hand-washing before eating for groundworkers',
        'Contaminated material disposed of by a licensed carrier',
      ]},
      { heading: '28. Waste management', body: '', items: [
        'Skips and segregated waste areas in place',
        'Waste removed regularly by licensed carriers, with transfer notes kept',
        'No burning of waste on site',
        'Hazardous waste stored and disposed of separately',
      ]},
      { heading: '29. Health & Safety File', body: '', items: [
        'Principal Designer told of any information needed for the H&S File',
        'As-built drawings, O&M manuals and certificates collected during the job',
        'Information on residual hazards (e.g. asbestos left in place) recorded',
        'H&S File contributions handed over at completion',
      ]},
    ],
    signatureRequired: true,
  },
  // NOTE: 'f10_notification_record' template removed in May 2026 — the
  // real F10 is filed at https://form.hse.gov.uk/f10 on HSE's
  // own portal. Capturing the returned reference number as a generated
  // PDF in our Documents library doesn't add value. The site-setup
  // checklist item for F10 now links directly to HSE's portal as an
  // external link. F10 reference number storage on the Site row is a
  // future enhancement if customers ask for it.

  // NOTE: 'site_rules' template removed in May 2026 — the CPP template
  // already has a 'Site rules' section (now tick-driven) which covers
  // the same content for the relevant site. Having a standalone Site
  // Rules document duplicated that content and confused users about
  // which one to use. Use CPP § 5 instead.
  {
    id: 'site_welfare_plan',
    title: 'Site Welfare Plan',
    category: 'cdm',
    scope: 'site',
    description: 'Site welfare provision per Schedule 2 of CDM 2015 — toilets, washing, drinking water, rest area.',
    fields: [
      { key: 'site_name', label: 'Site name', type: 'text', required: true },
      { key: 'welfare_provider', label: 'Welfare unit provider', type: 'text' },
      { key: 'workforce_peak', label: 'Peak workforce', type: 'number' },
    ],
    sections: [
      { heading: 'Toilets & hand-washing', body: 'Provision per Schedule 2 — sufficient toilets for the workforce, hand-washing with hot and cold water, soap and towels. Maintained daily.' },
      { heading: 'Drinking water', body: 'Mains-fed or bottled drinking water available throughout the working day, clearly marked. Cups provided.' },
      { heading: 'Rest & changing facilities', body: 'Heated rest area with tables and seating. Means to heat food and water. Drying / changing area where work is wet, dirty or in cold conditions. Lockable storage for personal effects.' },
      { heading: 'Cleaning regime', body: 'Welfare facilities cleaned at least daily. Hand-washing supplies replenished. Toilets serviced per the welfare provider\'s contract.' },
    ],
    signatureRequired: false,
  },
  {
    id: 'site_fire_plan',
    title: 'Site Fire Plan & Muster Point',
    category: 'cdm',
    scope: 'site',
    description: 'Site fire plan — alarms, extinguishers, escape routes, muster point. Compliant with Regulatory Reform (Fire Safety) Order 2005.',
    fields: [
      { key: 'site_name', label: 'Site name', type: 'text', required: true },
      { key: 'muster_point', label: 'Muster point location', type: 'text', required: true },
      { key: 'responsible_person', label: 'Responsible Person', type: 'text', required: true },
    ],
    sections: [
      { heading: 'Fire risk assessment', body: 'A site-specific Fire Risk Assessment has been carried out and is on file. Significant hazards: hot works, flammable materials storage, electrical, smoking. Reviewed weekly during occupation.' },
      { heading: 'Detection & alarm', body: 'Alarm system appropriate to the site — typically klaxon / air-horn during early phases, panel-based system once dwelling is enclosed. Tested weekly.' },
      { heading: 'Means of escape', body: 'Escape routes signed and kept clear at all times. Two means of escape from any working area where reasonably practicable. Emergency lighting where natural light insufficient.' },
      { heading: 'Extinguishers', body: 'CO₂ + foam at every welfare cabin and at hot works locations. Annually serviced. Operatives briefed on which extinguisher for which fire type.' },
      { heading: 'Hot works', body: 'Hot Works Permit mandatory. Combustibles cleared 10 m. Fire watch 60 min after last spark. No hot works in last 90 min of working day.' },
      { heading: 'Evacuation', body: 'On hearing the alarm — STOP work, secure tools, leave by nearest route to the muster point. Roll call by the site manager. Do not re-enter until the site manager confirms safe.' },
    ],
    signatureRequired: false,
  },
  {
    id: 'site_emergency_procedures',
    title: 'Site Emergency Procedures',
    category: 'cdm',
    scope: 'site',
    description: 'Site emergency response procedures for fire, injury, service strike, near-miss.',
    fields: [
      { key: 'site_name', label: 'Site name', type: 'text', required: true },
      { key: 'nearest_ae', label: 'Nearest A&E', type: 'text', required: true },
      { key: 'site_manager', label: 'Site manager', type: 'text', required: true },
      { key: 'site_manager_mobile', label: 'Site manager mobile', type: 'text', required: true },
    ],
    sections: [
      { heading: 'Fire', body: 'Sound the alarm, evacuate to the muster point, do not re-enter. Site manager calls 999 if not already done. Roll call. Brief HSE if RIDDOR-reportable.' },
      { heading: 'Serious injury', body: 'Stop work. First-aider attends. Call 999 for any: loss of consciousness, breathing difficulty, suspected fracture / spinal injury, severe bleeding. Do not move casualty if back / neck pain. Site manager notifies emergency services and the next of kin.' },
      { heading: 'Service strike', body: 'STOP. Evacuate immediate area. Gas — call 0800 111 999. Electric — DNO emergency. Water — site water board. Do not approach until isolated.' },
      { heading: 'Asbestos uncovered', body: 'STOP. Cordon the area. Notify site manager who pauses all works in the area and arranges sample test. Licensed contractor for any licensable disturbance.' },
      { heading: 'Near-miss', body: 'Reported to site manager within the shift. Recorded in the digital Accident Book. Investigated; lessons shared.' },
      { heading: 'Severe weather', body: 'Wind >25 mph — stop external works at height. Lightning — clear scaffolds and roofs. Frost / ice — stop external concrete and tile work.' },
    ],
    signatureRequired: false,
  },
  {
    id: 'traffic_management_plan',
    title: 'Traffic Management Plan',
    category: 'cdm',
    scope: 'site',
    description: 'Site traffic management — vehicle routes, pedestrian routes, banksman use, deliveries.',
    fields: [
      { key: 'site_name', label: 'Site name', type: 'text', required: true },
      { key: 'tm_lead', label: 'Traffic management lead', type: 'text', required: true },
    ],
    sections: [
      {
        heading: 'Vehicle routes',
        body: '',
        hint: 'Tick the rules that apply on this site. Add any extras.',
        items: [
          'One-way traffic flow where possible',
          'Designated entry and exit points',
          'Internal roads kept tidy and surfaced for expected loads',
          '5 mph speed limit on site',
          'No reversing without a banksman',
        ],
      },
      {
        heading: 'Pedestrian routes',
        body: '',
        hint: 'Tick the controls in place for pedestrians on site.',
        items: [
          'Segregated pedestrian routes from welfare to working areas',
          'Pedestrian-only signs and barriers where shared with vehicles',
          'No-go zones marked around plant and excavations',
          'Crossing points clearly identified',
          'Visitors escorted at all times',
        ],
      },
      {
        heading: 'Banksman use',
        body: '',
        hint: 'Tick when a banksman is required on this site.',
        items: [
          'Banksman for any reverse manoeuvre',
          'Banksman for any lifting operation',
          'Banksman for tight squeezes and narrow access',
          'Banksman in radio contact with operator',
          'Reverse alarms and beacons mandatory on all plant',
          'Banksman in Class 3 hi-vis at all times',
        ],
      },
      {
        heading: 'Deliveries',
        body: '',
        hint: 'Tick the delivery rules that apply.',
        items: [
          'Delivery slots booked in advance to avoid congestion',
          'Drivers stay with vehicle or report to site cabin',
          'Tippers offload only to designated drop areas',
          'No deliveries outside agreed hours',
          'Deliveries supervised by banksman',
        ],
      },
      {
        heading: 'Public interaction',
        body: '',
        hint: 'Tick the controls where the site interacts with the public.',
        items: [
          'Signage where site abuts public footpath / road',
          'Heras / hoarding to perimeter',
          'Banksman during all vehicle movements at the boundary',
          'Lighting outside daylight hours',
          'Council street works / scaffold licence in place where required',
          'Neighbours notified of high-impact deliveries',
        ],
      },
    ],
    signatureRequired: false,
  },
  // NOTE: 'site_induction_record', 'visitor_log' and 'site_inspection_log'
  // templates were removed in May 2026 — they duplicated real flows that
  // already exist as first-class features:
  //   - Subcontractor inductions: /sites/:id/forms/subbie (FormSubbie.tsx)
  //     — captures person, insurance, RAMS, training, signature + photos.
  //   - Visitor inductions: the QR sign-in flow (PublicSignIn.tsx)
  //     — visitor scans the cabin QR, signs in on their own phone.
  //   - Site inspections: dashboard tick-list (daily/weekly/monthly).
  //   - Visitor capture: digital sign-in register.
  // Having Documents-library duplicates of these was confusing users
  // about where to do the real work. The relevant underlying registers
  // already export PDFs for audit / HSE visits.
  // Having them in the Documents library was double-listing the same work
  // and confusing users about where to actually do site inspections /
  // capture visitors (answer: from the dashboard tick-list and QR sign-in
  // flows, not the Documents tab). If a customer needs a printable PDF of
  // those records, both flows export PDFs from the relevant registers.
];

export const DOC_CATEGORIES: { id: DocCategory; label: string }[] = [
  { id: 'cdm',         label: 'CDM' },
  { id: 'policy',      label: 'Policy' },
  { id: 'register',    label: 'Register' },
  { id: 'certificate', label: 'Certificate' },
  { id: 'custom',      label: 'Custom upload' },
];
